// Client-side export of a supplier GTC amendment (fe#123 §3): .docx via `docx`,
// .pdf via `jspdf`. Libraries load on demand so they stay out of the main
// bundle. `gtc` = supplier-detail / procurement-GTC tree with amendment fields
// on each clause (isAmend, amendDetail, amendedAt) + amendStatus /
// amendedClauseCount.

import { monthName } from '../data/gtcApi';
import {
  countAmended,
  formatAmendDate,
  sortByOrder,
} from '../../../Components/Gtc/gtcAmendSummary';

export const NO_AMEND_TEXT =
  'No Amendment Proposed (Comply with Buyer GTC Clause)';

// ponytail: jsPDF's built-in Helvetica only covers WinAnsi (Latin-1 + cp1252
// extras such as the em dash); anything else prints as '?'. Embed a TTF font
// via addFileToVFS/addFont if non-Latin text (CJK, Arabic, ...) is needed.
const WINANSI_EXTRA = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';
export const pdfSafe = value =>
  Array.from(String(value ?? ''))
    .map(ch => {
      const code = ch.codePointAt(0);
      const ok =
        ch === '\n' ||
        ch === '\t' ||
        (code >= 0x20 && code <= 0x7e) ||
        (code >= 0xa0 && code <= 0xff) ||
        WINANSI_EXTRA.includes(ch);
      return ok ? ch : '?';
    })
    .join('');

const safeName = value => String(value || '').replace(/[^a-zA-Z0-9]/g, '_');

// Pure: tree -> rows the Word / PDF writers print. Exported for tests.
export const buildAmendDocModel = (gtc = {}) => {
  const amended = gtc.amendedClauseCount ?? countAmended(gtc);
  const amendStatus =
    gtc.amendStatus || (amended > 0 ? 'Amended' : 'Not Amended');
  const period = [monthName(gtc.month), gtc.year].filter(Boolean).join(' ');
  return {
    fileBase: `${safeName(gtc.gtcNumber)}_${safeName(gtc.title)}_Amend_GTC`,
    gtcNumber: gtc.gtcNumber || '-',
    title: gtc.title || '-',
    period,
    amended,
    amendStatus,
    subtitle: `${gtc.gtcNumber || '-'} — ${gtc.title || '-'} (${period})`,
    info: [
      ['ID GTC', gtc.gtcNumber || '-'],
      ['Edition Period', period || '-'],
      ['GTC Title', gtc.title || '-'],
      ['Amendment Status', `${amendStatus} (${amended} Clauses Amended)`],
    ],
    categories: sortByOrder(gtc.categories).map(category => ({
      name: category.name || '-',
      articles: sortByOrder(category.articles).map(article => ({
        number: article.number || '',
        title: article.title || '',
        rows: sortByOrder(article.clauses).map(clause => ({
          clause: `Clause ${clause.clauseNumber ?? ''}`.trim(),
          buyer: clause.content || '-',
          isAmend: Boolean(clause.isAmend),
          isAmendText: clause.isAmend ? 'ON (Amend)' : 'OFF',
          amendDetail: clause.isAmend ? clause.amendDetail || '-' : '',
          savedAt:
            clause.isAmend && clause.amendedAt
              ? formatAmendDate(clause.amendedAt)
              : '',
        })),
      })),
    })),
  };
};

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/* -------------------------------- Word --------------------------------- */

export const downloadGtcAmendDocx = async gtc => {
  const {
    AlignmentType,
    Document,
    HeadingLevel,
    Packer,
    Paragraph,
    ShadingType,
    Table,
    TableCell,
    TableRow,
    TextRun,
    WidthType,
  } = await import('docx');
  const m = buildAmendDocModel(gtc);

  const pct = size => ({ size, type: WidthType.PERCENTAGE });
  const shade = fill => ({ fill, type: ShadingType.CLEAR, color: 'auto' });
  const para = (text, opts = {}) =>
    new Paragraph({
      children: [new TextRun({ text, size: 18, ...opts })],
    });
  const cell = (children, width, fill) =>
    new TableCell({
      width: pct(width),
      shading: fill ? shade(fill) : undefined,
      children: Array.isArray(children) ? children : [children],
    });
  const italic = text => para(text, { italics: true, color: '64748B' });

  const children = [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: 'MASTER AMEND GTC — SUPPLIER AMENDMENT DOCUMENT',
          bold: true,
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
      children: [new TextRun({ text: m.subtitle, size: 20 })],
    }),
    new Table({
      width: pct(100),
      rows: m.info.map(
        ([label, value]) =>
          new TableRow({
            children: [
              cell(para(label, { bold: true }), 30, 'F1F5F9'),
              cell(para(value), 70),
            ],
          })
      ),
    }),
  ];

  if (!m.categories.length) {
    children.push(
      italic('No categories or clauses defined for this GTC document.')
    );
  }

  m.categories.forEach(category => {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 300 },
        children: [new TextRun({ text: category.name, bold: true })],
      })
    );
    if (!category.articles.length)
      children.push(italic('No articles in this category.'));

    category.articles.forEach(article => {
      children.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200 },
          children: [
            new TextRun({
              text: `Article ${article.number} — ${article.title}`,
              bold: true,
            }),
          ],
        })
      );
      if (!article.rows.length) {
        children.push(italic('No clauses in this article.'));
        return;
      }
      const head = new TableRow({
        tableHeader: true,
        children: [
          ['Clause', 12],
          ['GTC Clause from Buyer', 40],
          ['Is Amend', 12],
          ['Amendment Detail Information (Supplier)', 36],
        ].map(([text, width]) =>
          cell(para(text, { bold: true }), width, 'E2E8F0')
        ),
      });
      const rows = article.rows.map(row => {
        const fill = row.isAmend ? 'F0F9FF' : undefined;
        const amendCell = row.isAmend
          ? [
              para(`Proposed Amendment: ${row.amendDetail}`),
              ...(row.savedAt ? [italic(`(Saved: ${row.savedAt})`)] : []),
            ]
          : [italic(NO_AMEND_TEXT)];
        return new TableRow({
          children: [
            cell(para(row.clause, { bold: true }), 12, fill),
            cell(
              row.buyer.split('\n').map(line => para(line)),
              40,
              fill
            ),
            cell(para(row.isAmendText, { bold: row.isAmend }), 12, fill),
            cell(amendCell, 36, fill),
          ],
        });
      });
      children.push(new Table({ width: pct(100), rows: [head, ...rows] }));
    });
  });

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  triggerDownload(blob, `${m.fileBase}.docx`);
};

/* -------------------------------- PDF ---------------------------------- */

export const downloadGtcAmendPdf = async gtc => {
  const { jsPDF } = await import('jspdf');
  const m = JSON.parse(JSON.stringify(buildAmendDocModel(gtc)), (_k, v) =>
    typeof v === 'string' ? pdfSafe(v) : v
  );
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const MARGIN = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - MARGIN * 2;
  const LINE = 4.2;
  let y = MARGIN;

  const ensure = h => {
    if (y + h > pageH - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };
  const font = (style, size, rgb = [30, 41, 59]) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(...rgb);
  };

  // Header bar
  doc.setFillColor(0, 90, 156);
  doc.rect(MARGIN, y, contentW, 18, 'F');
  font('bold', 12, [255, 255, 255]);
  doc.text('MASTER AMEND GTC — SUPPLIER AMENDMENT SUMMARY', MARGIN + 4, y + 7);
  font('normal', 9, [255, 255, 255]);
  doc.text(
    `${m.gtcNumber} | ${m.period} | Status: ${m.amendStatus} (${m.amended} Amended Clauses)`,
    MARGIN + 4,
    y + 13
  );
  y += 24;

  font('bold', 13);
  doc.splitTextToSize(m.title, contentW).forEach(line => {
    ensure(6);
    doc.text(line, MARGIN, y);
    y += 6;
  });
  y += 2;

  if (!m.categories.length) {
    font('italic', 10, [100, 116, 139]);
    doc.text(
      'No categories or clauses available in this GTC document.',
      MARGIN,
      y + 4
    );
  }

  const colW = (contentW - 8) / 2;
  m.categories.forEach(category => {
    ensure(12);
    doc.setFillColor(241, 245, 249);
    doc.rect(MARGIN, y, contentW, 8, 'F');
    font('bold', 10.5);
    doc.text(
      doc.splitTextToSize(category.name, contentW - 6)[0],
      MARGIN + 3,
      y + 5.5
    );
    y += 11;

    category.articles.forEach(article => {
      ensure(8);
      font('bold', 10, [0, 90, 156]);
      doc.text(
        doc.splitTextToSize(
          `Article ${article.number} | ${article.title}`,
          contentW
        )[0],
        MARGIN,
        y + 4
      );
      y += 7;

      article.rows.forEach(row => {
        const fill = row.isAmend ? [239, 246, 255] : [248, 250, 252];
        const band = h => {
          doc.setFillColor(...fill);
          doc.rect(MARGIN, y, contentW, h, 'F');
        };
        font('normal', 8.5);
        const left = doc.splitTextToSize(row.buyer, colW);
        const right = doc.splitTextToSize(
          row.isAmend ? row.amendDetail : NO_AMEND_TEXT,
          colW
        );

        ensure(LINE * 4);
        band(LINE * 3 + 2);
        font('bold', 9);
        doc.text(row.clause, MARGIN + 3, y + 4.5);
        font('normal', 8.5, row.isAmend ? [0, 90, 156] : [100, 116, 139]);
        doc.text(
          `Is Amend: ${row.isAmend ? 'ON' : 'OFF'}${row.savedAt ? ` (Saved ${row.savedAt})` : ''}`,
          MARGIN + 3 + colW / 2,
          y + 4.5
        );
        font('bold', 7.5, [100, 116, 139]);
        doc.text('GTC CLAUSE FROM BUYER', MARGIN + 3, y + 9.5);
        doc.text('AMENDMENT DETAIL INFORMATION', MARGIN + 5 + colW, y + 9.5);
        y += LINE * 3 + 2;

        // Line by line so a long clause can flow over pages.
        font('normal', 8.5);
        const lines = Math.max(left.length, right.length);
        for (let i = 0; i < lines; i += 1) {
          ensure(LINE);
          band(LINE);
          font('normal', 8.5);
          if (left[i]) doc.text(left[i], MARGIN + 3, y + 3.2);
          if (right[i]) doc.text(right[i], MARGIN + 5 + colW, y + 3.2);
          y += LINE;
        }
        band(2);
        y += 4;
      });
      y += 2;
    });
  });

  doc.save(`${m.fileBase}.pdf`);
};
